/**
 * Client accessors for the staff waitlist surface (backend `StaffWaitlistController`, DOMAIN.md §C.9).
 * Every call funnels through `@repo/api-client`'s fetch mutator (bearer token, RFC 9457 `ApiError`,
 * auto Idempotency-Key on writes). The response/request shapes are declared locally here until the
 * OpenAPI-generated models land (`just gen`); the field names mirror the controller DTOs exactly so the
 * swap is a type import change, nothing more. Thin accessors (not generated hooks) so queries key under
 * the shell's `["venue", venueId, "waitlist", …]` convention and realtime `waitlist.*` invalidation
 * reaches this view through one wiring (eventQueryKeyMap).
 */
import { customInstance } from "@repo/api-client";

export type WaitlistQueue = "RESERVATION_WAITLIST" | "WALKIN_STANDBY";

export type WaitlistEntry = {
  id: string;
  externalEntryId: string;
  queue: WaitlistQueue;
  state: string;
  googleState: string;
  position: number;
  partySize: number;
  guestGivenName: string | null;
  guestFamilyName: string | null;
  phone: string | null;
  email: string | null;
  quotedWaitMinutes: number | null;
  contactFlagged: boolean;
  blacklistFlagged: boolean;
  deliveryFailed: boolean;
  responseWindowEndsAtSec: number | null;
  resultingBookingId: string | null;
  joinedAtSec: number;
  notifiedAtSec: number | null;
  checkedInAtSec: number | null;
  seatedAtSec: number | null;
  version: number;
};

export type WaitlistListResponse = { entries: WaitlistEntry[] };
export type ProposeNextResponse = {
  queue: WaitlistQueue;
  freedCovers: number;
  entry: WaitlistEntry | null;
};
export type CloseQueueResponse = { queue: WaitlistQueue; declinedCount: number };

export type StaffJoinRequest = {
  serviceId: string;
  queue?: WaitlistQueue;
  givenName?: string;
  familyName?: string;
  phone?: string;
  email?: string;
  partySize: number;
  quotedWaitMinutes?: number;
};

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/waitlist`;

export const waitlistApi = {
  /** The single staff view: every OPEN entry across BOTH queues (R6). Readable even when the flag is off (WL-6). */
  list: (venueId: string, signal?: AbortSignal) =>
    customInstance<WaitlistListResponse>(`${base(venueId)}/entries`, { method: "GET", signal }),

  /** Staff add to either queue (door quick form). */
  add: (venueId: string, body: StaffJoinRequest) =>
    post<WaitlistEntry>(`${base(venueId)}/entries`, body),

  /** Notify (page) an entry for a freed capacity (WL-2/WL-4). Override needs a reason. */
  notify: (
    venueId: string,
    id: string,
    version: number,
    freedCovers: number,
    overrideReason?: string,
  ) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/notify`, {
      version,
      freedCovers,
      overrideReason,
    }),

  checkIn: (venueId: string, id: string, version: number) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/check-in`, { version }),

  /** Seat: claim via SlotClaimPort (channel WAITLIST) + WAITLIST booking SEATED + entry → SEATED. */
  seat: (venueId: string, id: string, version: number, tableIds?: string[]) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/seat`, {
      version,
      tableIds,
    }),

  cancel: (venueId: string, id: string, version: number) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/cancel`, { version }),

  decline: (venueId: string, id: string, version: number) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/decline`, { version }),

  reorder: (venueId: string, id: string, version: number, position: number) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/reorder`, {
      version,
      position,
    }),

  editPartySize: (venueId: string, id: string, version: number, partySize: number) =>
    post<WaitlistEntry>(`${base(venueId)}/entries/${encodeURIComponent(id)}/party-size`, {
      version,
      partySize,
    }),

  closeQueue: (venueId: string, serviceId: string, queue: WaitlistQueue) =>
    post<CloseQueueResponse>(`${base(venueId)}/close`, { serviceId, queue }),
};

export function guestLabel(e: WaitlistEntry): string {
  const name = [e.guestGivenName, e.guestFamilyName].filter(Boolean).join(" ").trim();
  return name || e.phone || e.email || "—";
}

function post<T>(url: string, body: unknown): Promise<T> {
  return customInstance<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? "{}" : JSON.stringify(body),
  });
}
